<template>
  <SrpDropdown class="location-category-picker" placement="bottom-start" :offset="4" :width="190" :zIndex="1300">
    <template #clickableElement="{ isDropdownVisible }">
      <button type="button" class="location-category-picker__trigger" :title="triggerLabel" :aria-label="triggerLabel" :aria-expanded="isDropdownVisible">
        <LocationCategoryIcon v-if="selectedOption" :option="selectedOption" :size="24" />
        <span v-else class="location-category-picker__unset"><IconEmbedded name="grid_2" :size="14" color="currentColor" /></span>
      </button>
    </template>
    <template #dropdown="{ close, isDropdownVisible }">
      <div class="location-category-picker__menu" :inert="!isDropdownVisible" :aria-hidden="!isDropdownVisible" @keydown.esc.stop="close()">
        <button
          v-for="option in LOCATION_CATEGORY_OPTIONS"
          :key="option.value"
          type="button"
          :class="{ 'location-category-picker__choice--selected': props.modelValue === option.value }"
          @click="choose(option.value as LocationCategory, close)"
        >
          <LocationCategoryIcon :option="option" :size="24" />{{ option.label }}
        </button>
        <button v-if="props.modelValue" type="button" class="location-category-picker__clear" @click="choose(null, close)">No type</button>
      </div>
    </template>
  </SrpDropdown>
</template>

<script setup lang="ts">
import { computed } from "vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LocationCategoryIcon from "./LocationCategoryIcon.vue";
import { LOCATION_CATEGORY_OPTIONS } from "@logic/LocationCategories";
import type { LocationCategory } from "@contracts/socialLocations";

// zIndex 1300: LocationEditorModal opens at 1200, above the dropdown's default layer, so the menu would open behind it.
const props = defineProps<{ modelValue: LocationCategory | null }>();
const emit = defineEmits<{ (e: "update:modelValue", value: LocationCategory | null): void }>();

const selectedOption = computed(() => LOCATION_CATEGORY_OPTIONS.find(option => option.value === props.modelValue) ?? null);
const triggerLabel = computed(() => (selectedOption.value ? `Type: ${selectedOption.value.label} (click to change)` : "Set the type of place"));

function choose(value: LocationCategory | null, close: () => void) {
  emit("update:modelValue", value);
  close();
}
</script>

<style scoped>
.location-category-picker { flex: 0 0 auto; }
.location-category-picker__trigger { display: inline-flex; align-items: center; justify-content: center; padding: 2px; border: 0; border-radius: 50%; background: transparent; cursor: pointer; }
.location-category-picker__trigger:hover { background: rgba(0, 0, 0, 0.06); }
.location-category-picker__trigger:focus-visible { outline: 2px solid #087f82; outline-offset: 1px; }
.location-category-picker__unset { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border: 1px dashed #98a2b3; border-radius: 50%; color: #98a2b3; }
.location-category-picker__menu { padding: 5px; }
.location-category-picker__menu button { display: flex; align-items: center; gap: 10px; width: 100%; padding: 8px 10px; border: 0; border-radius: 4px; background: white; color: #28343b; font-size: 14px; font-weight: 400; cursor: pointer; text-align: left; white-space: nowrap; }
.location-category-picker__menu button:hover, .location-category-picker__menu .location-category-picker__choice--selected { background: #eef6f5; }
.location-category-picker__menu button:focus-visible { outline: 2px solid #087f82; outline-offset: -2px; }
.location-category-picker__menu .location-category-picker__clear { margin-top: 4px; padding-left: 12px; border-top: 1px solid #e7e9ed; border-radius: 0 0 4px 4px; color: #667085; }
</style>
